import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { useMFA } from '../../contexts/MFAContext';
import { useApi } from '../../contexts/ApiContext';
import { useWithTokenRefresh } from '../../utils/api';
import { MFATypes } from '@flashbacktech/flashbackclient/api';

interface MagicLinkSetupProps {
  onSetupComplete: () => void;
}

const MagicLinkSetup: React.FC<MagicLinkSetupProps> = ({ onSetupComplete }) => {
  const { mfaState, setupMFAMethod, clearError } = useMFA();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const [isSent, setIsSent] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);

  // Automatically initialize setup when component mounts
  useEffect(() => {
    // Small delay to show loading state, then proceed to email input
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleSetupClick = () => {
    setShowConfirm(true);
  };

  const handleSetupConfirmed = async () => {
    setShowConfirm(false);
    clearError();
    setIsSent(false);

    // Send magic link to user's record email (no email parameter needed)
    await setupMFAMethod(MFATypes.MFAType.MAGIC_LINK);
    setIsSent(true);
  };

  const handleCheckStatus = async () => {
    setIsChecking(true);
    clearError();
    try {
      const res = (await withTokenRefresh(() => apiClient.getAvailableMethods())) as {
        success?: boolean;
        data?: Array<{ type: string; isConfigured?: boolean; isEnabled?: boolean }>;
      };
      const methods = res?.data ?? [];
      const magicLinkMethod = methods.find((m) => m.type === MFATypes.MFAType.MAGIC_LINK);
      if (magicLinkMethod?.isConfigured && magicLinkMethod?.isEnabled) {
        onSetupComplete();
      }
      // If not yet enabled, user may not have clicked the link; leave them on the same screen
    } catch (error) {
      // ignore
    } finally {
      setIsChecking(false);
    }
  };

  const handleResend = () => {
    setIsSent(false);
  };

  const error = mfaState.error;
  const isSettingUp = mfaState.isSettingUp;

  // Card wrapper style
  const cardStyle: React.CSSProperties = {
    background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', overflow: 'hidden',
  };

  if (isInitializing) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: '40px 32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <CircularProgress size={22} sx={{ color: '#617BFF' }} />
          <span style={{ fontSize: '14px', color: '#535862' }}>Initializing Magic Link setup...</span>
        </div>
      </div>
    );
  }

  if (isSent) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: '32px', textAlign: 'center' }}>
          {/* Success icon */}
          <div style={{ width: 56, height: 56, borderRadius: '14px', background: '#ECFDF3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#079455" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#181D27', margin: '0 0 8px', letterSpacing: '-0.01em' }}>
            Magic Link Sent!
          </h3>
          <p style={{ fontSize: '14px', color: '#717680', margin: '0 0 8px', lineHeight: 1.5 }}>
            We've sent a verification link to your registered email address
          </p>
          <p style={{ fontSize: '13px', color: '#98A2B3', margin: '0 0 28px', lineHeight: 1.5 }}>
            Click the link in your email to complete the setup. The link will expire in 15 minutes.
          </p>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={handleCheckStatus}
              disabled={isChecking}
              style={{
                padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#617BFF',
                background: '#F0EDFF', border: '1px solid #D9D2FF', borderRadius: '10px',
                cursor: isChecking ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: '8px', opacity: isChecking ? 0.7 : 1,
              }}
            >
              {isChecking ? <CircularProgress size={16} sx={{ color: '#617BFF' }} /> : null}
              {isChecking ? 'Checking...' : 'Check Status'}
            </button>
            <button
              onClick={handleResend}
              style={{
                padding: '10px 24px', fontSize: '14px', fontWeight: 500, color: '#717680',
                background: 'transparent', border: 'none', borderRadius: '10px',
                cursor: 'pointer', transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#414651'; }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#717680'; }}
            >
              Resend Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={{ padding: '32px 32px 24px', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: '14px', background: '#EFF8FF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2E90FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
          </svg>
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#181D27', margin: '0 0 6px', letterSpacing: '-0.01em' }}>
          Magic Link Setup
        </h3>
        <p style={{ fontSize: '14px', color: '#717680', margin: 0, lineHeight: 1.5 }}>
          Receive a secure link via email to verify your identity
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{ margin: '0 32px 16px', padding: '12px 16px', borderRadius: '10px', background: '#FEF3F2', border: '1px solid #FECDCA', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
          <span style={{ fontSize: '13px', color: '#B42318', flex: 1 }}>{error}</span>
          <button onClick={clearError} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D92D20', padding: 0, fontSize: '18px', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Content */}
      <div style={{ padding: '0 32px 24px', textAlign: 'center' }}>
        <p style={{ fontSize: '14px', color: '#535862', margin: '0 0 24px', lineHeight: 1.5 }}>
          We'll send a magic link to your registered email address
        </p>

        <button
          onClick={handleSetupClick}
          disabled={isSettingUp}
          style={{
            width: '100%', padding: '12px', fontSize: '14px', fontWeight: 600, color: '#FFFFFF',
            background: isSettingUp ? '#B0BCFF' : '#617BFF',
            border: 'none', borderRadius: '10px', cursor: isSettingUp ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}
          onMouseEnter={(e) => { if (!isSettingUp) (e.target as HTMLElement).style.background = '#5A6FE8'; }}
          onMouseLeave={(e) => { if (!isSettingUp) (e.target as HTMLElement).style.background = '#617BFF'; }}
        >
          {isSettingUp ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
          )}
          {isSettingUp ? 'Sending...' : 'Send Magic Link'}
        </button>
      </div>

      {/* Info box */}
      <div style={{ margin: '0 32px 32px', padding: '14px 16px', borderRadius: '10px', background: '#F9FAFB', border: '1px solid #EAECF0' }}>
        <p style={{ fontSize: '12px', color: '#535862', margin: 0, lineHeight: 1.6 }}>
          <strong style={{ color: '#344054' }}>How it works:</strong> We'll send you a secure link via email. Click the link to verify your identity and complete the setup. This method is secure and doesn't require you to remember additional codes.
        </p>
      </div>

      {/* Confirmation overlay */}
      {showConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowConfirm(false)}>
          <div style={{ background: '#FFFFFF', borderRadius: '16px', width: 400, maxWidth: '90vw', padding: '28px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'linear-gradient(135deg, #2E90FA 0%, #617BFF 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#181D27', margin: '0 0 4px' }}>Enable Magic Link?</h3>
            <p style={{ fontSize: '13px', color: '#717680', lineHeight: 1.5, margin: '0 0 24px' }}>
              This will activate Magic Link as a two-factor authentication method on your account. A verification link will be sent to your registered email address.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{ padding: '10px 24px', fontSize: '13px', fontWeight: 600, color: '#535862', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSetupConfirmed}
                style={{ padding: '10px 24px', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', background: '#617BFF', border: 'none', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#5A6FE8'; }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.background = '#617BFF'; }}
              >
                Confirm & Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MagicLinkSetup;
